import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Field, change } from 'redux-form';
import Table from 'react-table';
import selectTableHOC from 'react-table/lib/hoc/selectTable';
import 'react-table/react-table.css';
import '../../../../config/columns.css';
import TextInput from '../../forms/input/TextInput';
import {
  COLOR,
  DEFAULT_ORDER,
  DEFAULT_PAGE_SIZE,
  DEFAULT_SORT,
} from '../../../../config/constants';

const SelectTable = selectTableHOC(Table);

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const MAX_PAGE_SIZE = 100;

const EnterFilterInput = ({ filter, onChange }) => {
  const [value, setValue] = useState(filter ? filter.value : '');

  useEffect(() => {
    setValue(filter ? filter.value : '');
  }, [filter]);

  return (
    <input
      type="text"
      style={{ width: '100%' }}
      value={value || ''}
      onChange={(e) => {
        setValue(e.target.value);
        onChange(e.target.value);
      }}
    />
  );
};

EnterFilterInput.propTypes = {
  filter: PropTypes.shape({
    id: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  }),
  onChange: PropTypes.func.isRequired,
};

EnterFilterInput.defaultProps = {
  filter: null,
};

const isEmptyFilterValue = (value) => {
  if (value === undefined || value === null) return true;
  if (Array.isArray(value)) return value.length === 0;
  return String(value).trim() === '';
};

const buildQuery = (filtered = [], filterQueryByColumnId = {}) =>
  filtered
    .filter((f) => !isEmptyFilterValue(f.value))
    .map((f) => {
      const value = Array.isArray(f.value)
        ? f.value.join('|')
        : String(f.value).trim();

      const customQuery = filterQueryByColumnId[f.id];
      return customQuery ? customQuery(value) : `${f.id}:${value}`;
    });

const buildSort = (sorted = []) => {
  const sort = sorted.map(({ id, desc }) => `${id},${desc ? 'desc' : 'asc'}`);
  return sort.length > 0 ? sort : DEFAULT_SORT;
};

const EnterFilterSelectionableTable = ({
  columns,
  data,
  defaultPageSize,
  pages,
  loading,
  keyField,
  form,
  name,
  required,
  isItemSelectable,
  queryProps,
  fetchData,
  params,
  refreshKey,
  maxPageSize,
  minRows,
  rowColorResolver,
  dispatchChange,
  ...rest
}) => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [filtered, setFiltered] = useState([]);
  const [sorted, setSorted] = useState([]);

  const draftFilteredRef = useRef([]);

  const [selection, setSelection] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const canSelect = useCallback(
    (row) => !isItemSelectable || isItemSelectable(row),
    [isItemSelectable],
  );

  const updateSelection = (newSelection) => {
    setSelection(newSelection);
    dispatchChange(form, name, newSelection.length > 0 ? newSelection : null);
  };

  useEffect(() => {
    setSelection([]);
    setSelectAll(false);
    dispatchChange(form, name, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(
    () => () => {
      dispatchChange(form, name, null);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const filterParamByColumnId = useMemo(
    () =>
      columns.reduce((acc, col) => {
        const columnId = col.id || col.accessor;
        if (col.filterParam && typeof columnId === 'string') {
          acc[columnId] = col.filterParam;
        }
        return acc;
      }, {}),
    [columns],
  );

  const filterQueryByColumnId = useMemo(
    () =>
      columns.reduce((acc, col) => {
        const columnId = col.id || col.accessor;
        if (typeof col.filterQuery === 'function' && typeof columnId === 'string') {
          acc[columnId] = col.filterQuery;
        }
        return acc;
      }, {}),
    [columns],
  );

  const onFetchData = (state) => {
    if (!fetchData) return;

    const queryFilters = [];
    const paramFilters = {};

    (state.filtered || []).forEach((f) => {
      if (isEmptyFilterValue(f.value)) return;

      const paramName = filterParamByColumnId[f.id];
      if (paramName) {
        paramFilters[paramName] = Array.isArray(f.value) ? f.value : [f.value];
      } else {
        queryFilters.push(f);
      }
    });

    const query = buildQuery(queryFilters, filterQueryByColumnId);
    if (queryProps) {
      query.push(queryProps);
    }

    fetchData({
      ...params,
      ...paramFilters,
      page: state.page || 0,
      size: state.pageSize || DEFAULT_PAGE_SIZE,
      sort: buildSort(state.sorted),
      order: DEFAULT_ORDER,
      query,
    });
  };

  const updateDraft = useCallback((columnId, value) => {
    const others = draftFilteredRef.current.filter((f) => f.id !== columnId);

    draftFilteredRef.current = isEmptyFilterValue(value)
      ? others
      : [...others, { id: columnId, value }];
  }, []);

  const renderEnterFilter = ({ filter, column }) => (
    <EnterFilterInput
      filter={filter}
      onChange={(value) => updateDraft(column.id, value)}
    />
  );

  const draftColumns = useMemo(
    () =>
      columns.map((col) => {
        if (!col.Filter || col.filterable === false) return col;

        const OriginalFilter = col.Filter;

        return {
          ...col,
          Filter: (filterProps) =>
            OriginalFilter({
              ...filterProps,
              onChange: (value) => updateDraft(filterProps.column.id, value),
            }),
        };
      }),
    [columns, updateDraft],
  );

  const handleKeyDown = (e) => {
    if (e.key !== 'Enter') return;

    if (e.defaultPrevented) return;

    const { target } = e;
    if (!(target instanceof HTMLElement)) return;
    if (!target.closest('.rt-thead.-filters')) return;

    e.preventDefault();

    setFiltered([...draftFilteredRef.current]);
    setPage(0);
  };

  const isFirstRefreshRef = useRef(true);

  useEffect(() => {
    if (isFirstRefreshRef.current) {
      isFirstRefreshRef.current = false;
      return;
    }

    const nextFiltered = [...draftFilteredRef.current];

    const stateChanged =
      JSON.stringify(nextFiltered) !== JSON.stringify(filtered) ||
      page !== 0 ||
      pageSize !== defaultPageSize;

    if (stateChanged) {
      setFiltered(nextFiltered);
      setPage(0);
      setPageSize(defaultPageSize);
    } else {
      onFetchData({ page: 0, pageSize, sorted, filtered });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryProps, refreshKey]);

  const toggleSelection = (_key, _shift, row) => {
    if (!canSelect(row)) return;

    const key = row[keyField];
    const newSelection = selection.includes(key)
      ? selection.filter((k) => k !== key)
      : [...selection, key];

    updateSelection(newSelection);
  };

  const toggleAll = () => {
    const newSelectAll = !selectAll;
    const newSelection = newSelectAll
      ? data.filter(canSelect).map((row) => row[keyField])
      : [];

    setSelectAll(newSelectAll);
    updateSelection(newSelection);
  };

  const isSelected = (key) => selection.includes(key);

  // eslint-disable-next-line react/prop-types
  const SelectInputComponent = ({ row }) => {
    if (!canSelect(row)) return null;
    const key = row[keyField];
    return (
      <input
        type="checkbox"
        checked={isSelected(key)}
        onChange={() => {}}
        onClick={(e) => {
          e.stopPropagation();
          toggleSelection(key, false, row);
        }}
      />
    );
  };

  const getTrProps = (state, rowInfo) => {
    let background;

    if (rowInfo && selection.includes(rowInfo.original[keyField])) {
      background = COLOR.selectedRow;
    } else if (rowInfo && rowColorResolver) {
      background = rowColorResolver(rowInfo.original) || undefined;
    }

    return {
      onClick: (e, handleOriginal) => {
        if (handleOriginal) {
          handleOriginal();
        } else if (rowInfo && canSelect(rowInfo.original)) {
          toggleSelection(rowInfo.original[keyField], false, rowInfo.original);
        }
      },
      style: { background },
    };
  };

  return (
    <>
      <Field
        name={name}
        component={TextInput}
        type="hidden"
        validate={
          required
            ? [
                (v) =>
                  v ? undefined : 'Debe seleccionar al menos un elemento de la tabla',
              ]
            : null
        }
      />
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div onKeyDown={handleKeyDown}>
        <SelectTable
          minRows={minRows}
          pageSizeOptions={PAGE_SIZE_OPTIONS.filter((size) => size <= maxPageSize)}
          {...rest}
          manual
          filterable
          columns={draftColumns}
          data={data}
          pages={pages}
          loading={loading}
          keyField={keyField}
          className="-striped -highlight"
          // estado controlado
          page={page}
          pageSize={pageSize}
          filtered={filtered}
          sorted={sorted}
          onPageChange={(p) => setPage(p)}
          onPageSizeChange={(size, p) => {
            setPageSize(Math.min(size, maxPageSize));
            setPage(p);
          }}
          onSortedChange={(newSorted) => {
            setSorted(newSorted);
            setPage(0);
          }}
          onFetchData={onFetchData}
          FilterComponent={renderEnterFilter}
          selectType="checkbox"
          isSelected={isSelected}
          toggleSelection={toggleSelection}
          selectAll={selectAll}
          toggleAll={toggleAll}
          SelectInputComponent={SelectInputComponent}
          getTrProps={getTrProps}
        />
      </div>
    </>
  );
};

EnterFilterSelectionableTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ).isRequired,
  defaultPageSize: PropTypes.number,
  pages: PropTypes.number,
  loading: PropTypes.bool,
  keyField: PropTypes.string,
  form: PropTypes.string.isRequired,
  name: PropTypes.string,
  required: PropTypes.bool,
  isItemSelectable: PropTypes.func,
  queryProps: PropTypes.string,
  fetchData: PropTypes.func,
  params: PropTypes.shape({}),
  refreshKey: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  maxPageSize: PropTypes.number,
  minRows: PropTypes.number,
  rowColorResolver: PropTypes.func,
  dispatchChange: PropTypes.func.isRequired,
};

EnterFilterSelectionableTable.defaultProps = {
  defaultPageSize: DEFAULT_PAGE_SIZE,
  pages: undefined,
  loading: false,
  keyField: 'id',
  name: 'items',
  required: false,
  isItemSelectable: null,
  queryProps: '',
  fetchData: null,
  params: null,
  refreshKey: null,
  maxPageSize: MAX_PAGE_SIZE,
  minRows: 3,
  rowColorResolver: null,
};

const mapDispatchToProps = {
  dispatchChange: change,
};

export default connect(null, mapDispatchToProps)(EnterFilterSelectionableTable);